/**
 * "Encuéntralo en tienda" card covering the Regular Stock, Low Stock, and Out of Stock states (US-160133).
 */
import { AvailabilityStoreLocation } from '@/components/blocks/ProductDetails/parts/inventory/AvailabilityStoreLocation';
import { SearchInOtherStore } from '@/components/blocks/ProductDetails/parts/inventory/SearchInOtherStore';
import {
	availabilitySx,
	storeAvailabilityCardSx,
} from '@/components/blocks/ProductDetails/styles/inventoryAvailability';
import { useLocalization } from '@/data/Localization';
import { ProductType } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { StoreIcon } from 'presentation/svgImages/StoreIcon';
import { FC } from 'react';
import {
	OpenSansFontWeights,
	QUANTITY_AVAILABLE_PLUS_10,
	QUANTITY_AVAILABLE_PLUS_999,
} from 'shared/custom/constants/commonConstants';

interface Props {
	isB2B: boolean;
	bayData?: { aisle: string; area: string; bay: string };
	storeAvailability: number | string;
	storeName: string;
	locationName: string;
	showSearchInOtherStore: boolean;
	productId: string;
	isKitOrBundle: boolean;
	kitComponents: ProductType[];
	storeAvailProduct: ProductAvailabilityData;
	physicalStoreId: string;
}

export const StoreAvailabilityCard: FC<Props> = ({
	isB2B,
	bayData,
	storeAvailability,
	storeName,
	locationName,
	showSearchInOtherStore,
	productId,
	isKitOrBundle,
	kitComponents,
	storeAvailProduct,
	physicalStoreId,
}) => {
	const { Availability } = useLocalization('HDMProductDetail');

	const quantity = Math.floor(Number(storeAvailability) || 0);
	const isOutOfStock = quantity <= 0;
	const isLowStock = !isOutOfStock && quantity <= QUANTITY_AVAILABLE_PLUS_10;
	const quantityDisplay =
		quantity > QUANTITY_AVAILABLE_PLUS_999 ? Availability.labels.available999.t() : quantity;

	const stockStateClass = isOutOfStock
		? 'store-availability-card--out-of-stock'
		: isLowStock
		? 'store-availability-card--low-stock'
		: 'store-availability-card--regular-stock';

	return (
		<Paper
			sx={storeAvailabilityCardSx.card}
			elevation={1}
			id="extendedAvailabilityCard"
			className={`store-availability-card ${stockStateClass}`}
		>
			<Box sx={storeAvailabilityCardSx.mainContainer}>
				<Box sx={storeAvailabilityCardSx.title}>
					<Typography variant="headingSegment">{Availability.labels.findInStore.t()}</Typography>
				</Box>

				<Box sx={storeAvailabilityCardSx.contentContainer}>
					{!isOutOfStock && bayData ? (
						<AvailabilityStoreLocation isB2B={isB2B} bayData={bayData} />
					) : null}

					<Typography
						id="storeAvailabilityQuantity"
						variant="bodyBaseline"
						component="p"
						fontWeight={isOutOfStock ? OpenSansFontWeights.semibold : undefined}
						className={`store-availability-card__quantity${
							isLowStock ? ' store-availability-card__quantity--low' : ''
						}`}
						color={isOutOfStock ? 'grey.300' : isLowStock ? 'alert.main' : 'text.highlight'}
						sx={storeAvailabilityCardSx.quantityContainer}
					>
						{isOutOfStock ? (
							Availability.labels.empty.t()
						) : (
							<>
								{isLowStock ? Availability.labels.just.t() : ''}
								<Typography
									component="span"
									variant="bodyBaseline"
									fontWeight={OpenSansFontWeights.semibold}
								>
									{quantityDisplay}
								</Typography>
								{quantity === 1
									? Availability.labels.disponible.t()
									: Availability.labels.disponibles.t()}
							</>
						)}
					</Typography>

					<Box sx={storeAvailabilityCardSx.storeNameRow}>
						<StoreIcon sx={storeAvailabilityCardSx.storeNameIcon} />
						<Typography id="storeAvailabilityStoreName" variant="bodyBaseline" color="grey.500">
							{storeName}, {locationName}
						</Typography>
					</Box>

					{showSearchInOtherStore ? (
						<Box sx={storeAvailabilityCardSx.dividerLinkGroup}>
							<Divider sx={availabilitySx.divider} />
							<SearchInOtherStore
								productId={productId}
								isKitOrBundle={isKitOrBundle}
								kitComponents={kitComponents}
								storeAvailProduct={storeAvailProduct}
								physicalStoreId={physicalStoreId}
							/>
						</Box>
					) : null}
				</Box>
			</Box>
		</Paper>
	);
};
